import React, { useEffect, useState } from 'react';
import { getData } from '../global/Globalapi';

// --- Inline Icons (Colored to match the theme) ---
const Icons = {
  Users: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  Activity: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  ),
  Clock: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  Chart: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  ),
  Refresh: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
    </svg>
  )
};

const formatNumber = (num) => new Intl.NumberFormat('en-US').format(num);

// --- Card Component (White Card on Bronze BG) ---
const StatCard = ({ title, value, IconComponent }) => (
  <div className="bg-white/95 backdrop-blur rounded-2xl p-6 shadow-xl border border-white/20 transition-transform hover:-translate-y-1">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs font-bold text-[#a87245] uppercase tracking-wider mb-2">
          {title.replace(/_/g, ' ')}
        </p>
        <p className="text-3xl font-bold text-gray-800">
          {typeof value === 'number' ? formatNumber(value) : value}
        </p>
      </div>
      
      {/* Icon with Bronze colors */}
      <div className="rounded-xl p-3 bg-[#a87245]/10 text-[#a87245]">
        <IconComponent />
      </div>
    </div>
  </div>
);

const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  // Fetch analytics function
  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getData('/analytics/ga4-active-users');
      setData(res.data);
    } catch (err) {
      setError('Failed to fetch analytics data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line
  }, []);

  return (
    // MAIN BACKGROUND IS #a87245
    <div className="min-h-screen bg-[#a87245] p-6 md:p-12 font-sans">
      <div className="max-w-6xl mx-auto space-y-10">
        
        {/* Header - White text for contrast */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-2 border-b border-white/20">
          <div>
            <h1 className="text-4xl font-bold text-white tracking-tight">
              Analytics Overview
            </h1>
            <p className="mt-2 text-white/80 font-medium">
              Google Analytics 4 • Real-time Data
            </p>
          </div>
          
          <button
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-white text-[#a87245] hover:bg-gray-100 text-sm font-bold rounded-lg shadow-lg transition-colors"
            onClick={fetchAnalytics}
            disabled={loading}
            title="Refresh analytics data"
          >
            <Icons.Refresh />
            <span>Refresh</span>
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-pulse">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-36 bg-white/20 rounded-2xl"></div>
            ))}
          </div>
        ) : error ? (
          <div className="p-6 bg-white rounded-xl shadow-lg border-l-4 border-red-500 text-gray-800">
            <div className="font-bold mb-1">Error Loading Data</div>
            {error}
          </div>
        ) : data ? (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(data).slice(0, 3).map(([key, value]) => {
                let IconComponent = Icons.Activity;
                const lowerKey = key.toLowerCase();
                if (lowerKey.includes('user')) IconComponent = Icons.Users;
                if (lowerKey.includes('time')) IconComponent = Icons.Clock;
                if (lowerKey.includes('view')) IconComponent = Icons.Chart;

                return (
                  <StatCard 
                    key={key} 
                    title={key} 
                    value={value} 
                    IconComponent={IconComponent}
                  />
                );
              })}
            </div>

            {/* Main Table Container - White Panel */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-800">Detailed Metrics</h3>
                {/* Live Indicator */}
                <span className="flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-bold uppercase tracking-wider">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                  Live
                </span>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider border-b border-gray-100">
                      <th className="px-8 py-4 font-semibold">Metric Name</th>
                      <th className="px-8 py-4 font-semibold text-right">Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {Object.entries(data).map(([key, value], idx) => (
                      <tr key={key} className="hover:bg-gray-50 transition-colors">
                        <td className="px-8 py-2 text-gray-700 font-medium capitalize">
                          {key.replace(/_/g, ' ')}
                        </td>
                        <td className="px-8 py-3 text-right font-mono text-lg font-bold text-[#a87245]">
                          {typeof value === 'number' ? formatNumber(value) : value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="text-white/80 text-center py-10">
            No data available right now.
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;